import React, { FC, useState } from "react";
import NiceModal, { NiceModalHocProps, useModal } from "@ebay/nice-modal-react";
import { ErrorMessage, Field, Form, Formik, FieldInputProps } from "formik";
import * as Yup from "yup";
import KC_Modal_2 from "../../../designsystem/KC_Modal/KC_Modal_2";
import KC_Heading3 from "../../../designsystem/typography/KC-Heading/KC_Heading3";
import KC_Button, {
  KC_ButtonSize,
  KC_ButtonType
} from "../../../designsystem/KC_Button/KC_Button";
import { tt } from "../../../utils/i18n/i18n";
import type ManagedAccount from "@tootsweet/model/entities/ManagedAccount";
import { useDispatch } from "react-redux";
import fetchAllManagedAccounts from "../../../api/fetchAllManagedAccounts";
import KC_UIText from "../../../designsystem/typography/KC_UIText/KC_UIText";
import "./styles.scss";
import CONFIG from "../../../model/TSConfig";
import { setLoading } from "../../../redux/actions/app";
import { useJetpackConfigSelector } from "../../../redux/jetpackConfig";

interface Props {
  managedAccount: ManagedAccount;
}

interface FormValues {
  placeName: string;
  configName: string;
}

const nameValidation = Yup.string()
  .required(tt("required_field"))
  .min(2, tt("min_characters").replace("{{n}}", "2"))
  .max(50, tt("max_characters").replace("{{n}}", "50"));

const validationSchema = Yup.object().shape({
  placeName: nameValidation,
  configName: nameValidation
});

const EditPlaceNameModal: FC<Props & NiceModalHocProps> =
  NiceModal.create<Props>(({ managedAccount }) => {
    const modal = useModal();
    const dispatch = useDispatch();
    const config = useJetpackConfigSelector();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const initialValues: FormValues = {
      placeName: managedAccount.placeName || "",
      configName: config?.name || ""
    };

    const handleSubmit = async (values: FormValues) => {
      setIsSubmitting(true);
      setError(null);

      try {
        dispatch(setLoading(true));

        const response = await fetch(
          CONFIG.saasBackendUrl + "/customers/update-place-name",
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${managedAccount.token}`
            },
            body: JSON.stringify({
              placeName: values.placeName,
              configName: values.configName
            })
          }
        );

        if (!response.ok) {
          throw new Error(tt("error_updating_place_name"));
        }

        dispatch(fetchAllManagedAccounts({}));
        void modal.resolve();
        void modal.hide();
      } catch (err) {
        setError(err instanceof Error ? err.message : tt("error_generic"));
      } finally {
        setIsSubmitting(false);
        dispatch(setLoading(false));
      }
    };

    return (
      <KC_Modal_2
        id="edit-place-name-modal"
        isOpen={modal.visible}
        onClose={modal.hide}
        hideCloseButton={true}
        header={
          <div className="p-4">
            <KC_Heading3 text={tt("edit_place_name")} />
          </div>
        }
      >
        <div className="p-4">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isValid, values }) => {
              return (
                <Form>
                  <label htmlFor="placeName">{tt("internal_name")}</label>
                  <Field name="placeName">
                    {({ field }: { field: FieldInputProps<string> }) => (
                      <div className="kc-input-wrapper">
                        <input
                          {...field}
                          id="placeName"
                          className="kc_input kc-input-padding"
                          placeholder={tt("enter_internal_name")}
                        />
                      </div>
                    )}
                  </Field>
                  <ErrorMessage
                    name="placeName"
                    component="div"
                    className="error-message"
                  />

                  <label htmlFor="configName" className="mt-3 d-block">
                    {tt("public_name")}
                  </label>
                  <Field name="configName">
                    {({ field }: { field: FieldInputProps<string> }) => (
                      <div className="kc-input-wrapper">
                        <input
                          {...field}
                          id="configName"
                          className="kc_input kc-input-padding"
                          placeholder={tt("enter_public_name")}
                        />
                      </div>
                    )}
                  </Field>
                  <ErrorMessage
                    name="configName"
                    component="div"
                    className="error-message"
                  />

                  {error && (
                    <KC_UIText
                      text={error}
                      weight="reg"
                      className="d-block text-danger mt-2"
                    />
                  )}

                  <div className="d-flex justify-content-end mt-4 st-gap">
                    <KC_Button
                      text={tt("cancel")}
                      type={KC_ButtonType.Button}
                      onClick={modal.hide}
                      disabled={isSubmitting}
                      isInverted={true}
                      size={KC_ButtonSize.Large}
                    />
                    <KC_Button
                      text={tt("save")}
                      type={KC_ButtonType.Submit}
                      disabled={!isValid || isSubmitting}
                      isPrimary={true}
                      size={KC_ButtonSize.Large}
                    />
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </KC_Modal_2>
    );
  });

export default EditPlaceNameModal;
